import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import * as pdfjsLib from 'pdfjs-dist'
import { TextLayer } from 'pdfjs-dist'

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`

interface PDFViewerProps {
  url: string
  currentPage?: number
  onPageChange?: (page: number) => void
  onTotalPages?: (total: number) => void
  onCanvasResize?: (dimensions: { width: number; height: number }) => void
  overlay?: ReactNode
  renderPageOverlay?: (page: number, dims: { width: number; height: number }) => ReactNode
  className?: string
  textLayerRef?: React.RefObject<HTMLDivElement | null>
}

export function PDFViewer({
  url,
  currentPage = 1,
  onPageChange,
  onTotalPages,
  onCanvasResize,
  overlay,
  renderPageOverlay,
  className = '',
}: PDFViewerProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [scale, setScale] = useState(1.2)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pageDims, setPageDims] = useState<Record<number, { width: number; height: number }>>({})
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const isScrollingToPage = useRef(false)

  useEffect(() => {
    if (!url) return
    let cancelled = false

    const loadPDF = async () => {
      setLoading(true)
      setError(null)
      try {
        const doc = await pdfjsLib.getDocument(url).promise
        if (cancelled) return
        setPdfDoc(doc)
        setNumPages(doc.numPages)
        onTotalPages?.(doc.numPages)
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load PDF')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadPDF()
    return () => { cancelled = true }
  }, [url])

  // Track visible page via IntersectionObserver
  useEffect(() => {
    if (numPages === 0) return
    const container = scrollContainerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingToPage.current) return
        let maxRatio = 0
        let visiblePage = currentPage
        for (const entry of entries) {
          const page = Number(entry.target.getAttribute('data-page'))
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio
            visiblePage = page
          }
        }
        if (maxRatio > 0) {
          onPageChange?.(visiblePage)
        }
      },
      { root: container, threshold: [0, 0.25, 0.5, 0.75, 1] }
    )

    for (const [, el] of pageRefs.current) {
      observer.observe(el)
    }

    return () => observer.disconnect()
  }, [numPages, pageDims])

  // Scroll to page when currentPage changes from outside (e.g. toolbar)
  const scrollToPage = useCallback((page: number) => {
    const el = pageRefs.current.get(page)
    if (!el || !scrollContainerRef.current) return
    isScrollingToPage.current = true
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setTimeout(() => { isScrollingToPage.current = false }, 500)
  }, [])

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 rounded ${className}`}>
        <div className="text-center p-8">
          <p className="text-red-500 font-medium">Failed to load PDF</p>
          <p className="text-gray-500 text-sm mt-1">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`flex flex-col ${className}`} style={{ height: '100%' }}>
      {/* Toolbar */}
      <div className="flex items-center gap-4 p-2 bg-gray-800 text-white w-full shrink-0">
        <button
          onClick={() => {
            const p = Math.max(1, currentPage - 1)
            onPageChange?.(p)
            scrollToPage(p)
          }}
          disabled={currentPage <= 1}
          className="px-3 py-1 bg-gray-600 rounded disabled:opacity-50 hover:bg-gray-500 text-sm"
        >
          &lt;
        </button>
        <span className="text-sm">
          Page {currentPage} of {numPages}
        </span>
        <button
          onClick={() => {
            const p = Math.min(numPages, currentPage + 1)
            onPageChange?.(p)
            scrollToPage(p)
          }}
          disabled={currentPage >= numPages}
          className="px-3 py-1 bg-gray-600 rounded disabled:opacity-50 hover:bg-gray-500 text-sm"
        >
          &gt;
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setScale((s) => Math.max(0.5, s - 0.2))}
            className="px-3 py-1 bg-gray-600 rounded hover:bg-gray-500 text-sm"
          >
            -
          </button>
          <span className="text-sm">{Math.round(scale * 100)}%</span>
          <button
            onClick={() => setScale((s) => Math.min(3, s + 0.2))}
            className="px-3 py-1 bg-gray-600 rounded hover:bg-gray-500 text-sm"
          >
            +
          </button>
        </div>
      </div>

      {/* Scrollable pages */}
      <div
        ref={scrollContainerRef}
        className="bg-gray-200"
        style={{ overflow: 'auto', flex: '1 1 0', minHeight: 0 }}
      >
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        )}
        <div className="flex flex-col items-center gap-4 py-4">
          {pdfDoc && Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
            <PageRenderer
              key={pageNum}
              pdfDoc={pdfDoc}
              pageNum={pageNum}
              scale={scale}
              onDims={(dims) => {
                setPageDims((prev) => ({ ...prev, [pageNum]: dims }))
                if (pageNum === 1) onCanvasResize?.(dims)
              }}
              registerRef={(el) => {
                if (el) pageRefs.current.set(pageNum, el)
                else pageRefs.current.delete(pageNum)
              }}
              overlay={renderPageOverlay && pageDims[pageNum]
                ? renderPageOverlay(pageNum, pageDims[pageNum])
                : pageNum === currentPage ? overlay : null
              }
            />
          ))}
        </div>
      </div>
    </div>
  )
}

interface PageRendererProps {
  pdfDoc: pdfjsLib.PDFDocumentProxy
  pageNum: number
  scale: number
  onDims: (dims: { width: number; height: number }) => void
  registerRef: (el: HTMLDivElement | null) => void
  overlay?: ReactNode | null
}

function PageRenderer({ pdfDoc, pageNum, scale, onDims, registerRef, overlay }: PageRendererProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const textLayerRef = useRef<HTMLDivElement>(null)
  const renderTaskRef = useRef<any>(null)
  const [dims, setDims] = useState<{ width: number; height: number } | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return
    let cancelled = false

    const render = async () => {
      try {
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel()
        }

        const page = await pdfDoc.getPage(pageNum)
        if (cancelled) return
        const viewport = page.getViewport({ scale })
        const canvas = canvasRef.current!
        const context = canvas.getContext('2d')!

        canvas.height = viewport.height
        canvas.width = viewport.width

        const newDims = { width: viewport.width, height: viewport.height }
        setDims(newDims)
        onDims(newDims)

        renderTaskRef.current = page.render({ canvasContext: context, viewport })
        await renderTaskRef.current.promise

        if (textLayerRef.current && !cancelled) {
          textLayerRef.current.innerHTML = ''
          textLayerRef.current.style.setProperty('--scale-factor', '1')
          const textContent = await page.getTextContent()
          const textLayer = new TextLayer({
            textContentSource: textContent,
            container: textLayerRef.current,
            viewport,
          })
          await textLayer.render()
          textLayerRef.current.style.width = `${viewport.width}px`
          textLayerRef.current.style.height = `${viewport.height}px`
        }
      } catch (err: any) {
        if (err.name !== 'RenderingCancelledException') {
          console.error(`Failed to render page ${pageNum}:`, err)
        }
      }
    }

    render()
    return () => { cancelled = true }
  }, [pdfDoc, pageNum, scale])

  return (
    <div
      ref={registerRef}
      data-page={pageNum}
      className="relative"
      style={{ lineHeight: 0, flexShrink: 0 }}
    >
      <canvas ref={canvasRef} className="shadow-lg" style={{ display: 'block' }} />
      <div
        ref={textLayerRef}
        className="textLayer"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          overflow: 'hidden',
          opacity: 0.25,
          lineHeight: 1,
        }}
      />
      {overlay}
    </div>
  )
}

export { PDFViewer as default }
